import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { Seo, organizationJsonLd } from '@/components/Seo';
import { ArrowRight, Globe, ShieldCheck, Star, Headphones, ChevronLeft, ChevronRight } from 'lucide-react';

const HERO = '/images/categories/hero.jpg';
const DECANTER = '/images/categories/decanter.jpg';

const SectionTitle = ({ eyebrow, title, sub, dark }) => (
  <div className="text-center mb-10">
    {eyebrow && <div className="nw-eyebrow mb-3">{eyebrow}</div>}
    <h2 className={`font-serif text-3xl sm:text-4xl ${dark ? 'text-nw-black' : 'text-nw-ivory'}`}>{title}</h2>
    {sub && <p className={`mt-3 text-sm max-w-xl mx-auto ${dark ? 'text-[#5a4f42]' : 'text-nw-warmgrey'}`}>{sub}</p>}
  </div>
);

const ProductRow = ({ items }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
    {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
  </div>
);

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">{[0, 1, 2, 3].map((i) => <div key={i} className="bg-nw-espresso animate-pulse" style={{ aspectRatio: '3/4' }} />)}</div>
);

export default function Home() {
  const { t, lang, formatPrice } = useStore();
  const [data, setData] = useState(null);
  const [brandIdx, setBrandIdx] = useState(0);

  useEffect(() => { api.homepage().then(setData).catch(() => {}); }, []);

  const trust = [
    { icon: Globe, title: t.trust.shipping, sub: t.trust.shippingSub },
    { icon: ShieldCheck, title: t.trust.auth, sub: t.trust.authSub },
    { icon: Star, title: t.trust.rare, sub: t.trust.rareSub },
    { icon: Headphones, title: t.trust.advice, sub: t.trust.adviceSub },
  ];

  const seoTitle = lang === 'fr' ? 'NICEWINE — Grands vins & spiritueux rares, Paris' : 'NICEWINE — Fine Wines & Rare Spirits, Paris';
  const seoDesc = lang === 'fr'
    ? 'Maison parisienne d’import-export : Premiers Grands Crus, champagnes de prestige, cognacs et whiskies rares. Authenticité garantie, livraison assurée dans le monde entier.'
    : 'Parisian import-export house: First Growths, prestige champagnes, rare cognacs and whiskies. Guaranteed authenticity, insured worldwide delivery.';

  return (
    <div>
      <Seo title={seoTitle} description={seoDesc} path="/" image={HERO} lang={lang} jsonLd={organizationJsonLd(window.location.origin)} />
      {/* HERO */}
      <section data-testid="home-hero" className="relative min-h-[78vh] flex items-center overflow-hidden nw-vignette">
        <img src={HERO} alt={lang === 'fr' ? 'Grands vins et spiritueux NICEWINE' : 'NICEWINE fine wines and spirits'} fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-nw-black/85 via-nw-black/45 to-transparent" />
        <div className="relative nw-container">
          <div className="max-w-[640px] nw-fadeup">
            <div className="nw-eyebrow text-nw-gold mb-5">NICEWINE</div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] text-nw-ivory">
              {t.hero.title1}<br /><span className="text-nw-ivory">{t.hero.title2}</span>
            </h1>
            <p className="mt-6 text-nw-warmgrey max-w-md text-base leading-relaxed">{t.hero.subtitle}</p>
            <Link to="/collections" data-testid="home-hero-cta-button" className="nw-btn-gold mt-9">
              {t.hero.cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-nw-black border-b border-nw-gold/12" aria-label="Services">
        <div data-testid="home-trust-badges" className="nw-container py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {trust.map((b, i) => (
            <div key={i} className="flex items-center gap-4 justify-center lg:justify-start">
              <b.icon className="text-nw-gold shrink-0" size={26} strokeWidth={1} />
              <div>
                <div className="text-[11px] tracking-luxe uppercase text-nw-ivory">{b.title}</div>
                <div className="text-[11px] text-nw-warmgrey mt-1">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES (ivory) */}
      <section data-testid="home-categories-section" className="bg-nw-ivory">
        <div className="nw-container py-16 lg:py-24">
          <SectionTitle dark title={t.sections.categories} sub={t.sections.categoriesSub} />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {(data?.categories || []).map((c) => (
              <Link key={c.key} to={c.link} data-testid={`home-category-card-${c.key}`}
                className="group relative block overflow-hidden border border-[#00000018]" style={{ aspectRatio: '3/4' }}>
                <img src={c.image} alt={lang === 'fr' ? c.title_fr : c.title_en} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-nw-black/85 via-nw-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-center">
                  <h3 className="font-serif text-nw-ivory text-lg">{lang === 'fr' ? c.title_fr : c.title_en}</h3>
                  {c.from_price != null && (
                    <div className="text-nw-gold text-[11px] tracking-wide2 uppercase mt-1">{t.sections.from} {formatPrice(c.from_price)}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GRAND WINES */}
      <section className="bg-nw-black">
        <div className="nw-container py-16 lg:py-24">
          <SectionTitle eyebrow={t.sections.grandWinesSub} title={t.sections.grandWines} />
          {data ? <ProductRow items={(data.grand_wines || []).slice(0, 4)} /> : <Skeleton />}
          <div className="text-center mt-10"><Link to="/collections?category=wine" className="nw-btn-gold">{t.sections.viewAll}</Link></div>
        </div>
      </section>

      {/* EXCEPTIONAL SPIRITS */}
      <section className="bg-nw-espresso">
        <div className="nw-container py-16 lg:py-24">
          <SectionTitle eyebrow={t.sections.spiritsSub} title={t.sections.spirits} />
          {data ? <ProductRow items={(data.exceptional_spirits || []).slice(0, 4)} /> : <Skeleton />}
          <div className="text-center mt-10"><Link to="/collections?category=spirit" className="nw-btn-gold">{t.sections.viewAll}</Link></div>
        </div>
      </section>

      {/* COLLECTORS BANNER */}
      <section data-testid="home-collectors-banner" className="bg-nw-black">
        <div className="nw-container py-10">
          <div className="relative grid lg:grid-cols-2 border border-nw-gold/20 overflow-hidden">
            <div className="relative min-h-[320px]">
              <img src={DECANTER} alt={lang === 'fr' ? 'Carafe de cognac d’exception' : 'Exceptional cognac decanter'} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-nw-black/80" />
            </div>
            <div className="bg-nw-espresso p-10 lg:p-14 flex flex-col justify-center">
              <div className="nw-eyebrow text-nw-gold mb-3">{t.sections.exception}</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-nw-ivory">{t.sections.exceptionTitle}</h2>
              <p className="mt-4 text-nw-warmgrey text-sm leading-relaxed max-w-md">{t.sections.exceptionBody}</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="text-[11px] tracking-luxe uppercase text-nw-warmgrey">{t.sections.upTo}</span>
                <span className="font-serif text-nw-gold text-3xl">80 000 €+</span>
              </div>
              <Link to="/collections?rarity=collector" data-testid="home-collectors-cta-button" className="nw-btn-gold mt-8 self-start">{t.sections.viewCollection} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* RARE TREASURES */}
      <section className="bg-nw-black">
        <div className="nw-container py-16 lg:py-24">
          <SectionTitle eyebrow={t.sections.rareSub} title={t.sections.rare} />
          {data ? <ProductRow items={(data.rare_treasures || []).slice(0, 4)} /> : <Skeleton />}
        </div>
      </section>

      {/* BRANDS */}
      <section data-testid="home-brands-carousel" className="bg-nw-ivory">
        <div className="nw-container py-16">
          <SectionTitle dark title={t.sections.brands} sub={t.sections.brandsSub} />
          <BrandStrip brands={data?.brands || []} idx={brandIdx} setIdx={setBrandIdx} />
        </div>
      </section>
    </div>
  );
}

function BrandStrip({ brands, idx, setIdx }) {
  const perView = 5;
  const visible = brands.slice(idx, idx + perView);
  const canPrev = idx > 0;
  const canNext = idx + perView < brands.length;
  return (
    <div className="flex items-center gap-4">
      <button data-testid="home-brands-carousel-prev" aria-label="Previous" disabled={!canPrev} onClick={() => setIdx(Math.max(0, idx - 1))}
        className={`shrink-0 w-9 h-9 rounded-full border border-[#00000025] flex items-center justify-center transition-colors ${canPrev ? 'text-nw-black hover:border-nw-gold hover:text-nw-gold' : 'text-[#00000030]'}`}>
        <ChevronLeft size={18} />
      </button>
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {visible.map((b) => (
          <div key={b} className="h-24 flex items-center justify-center border border-[#00000012] px-3">
            <span className="font-serif text-[#2a2119] text-lg text-center leading-tight tracking-wide">{b}</span>
          </div>
        ))}
      </div>
      <button data-testid="home-brands-carousel-next" aria-label="Next" disabled={!canNext} onClick={() => setIdx(Math.min(brands.length - perView, idx + 1))}
        className={`shrink-0 w-9 h-9 rounded-full border border-[#00000025] flex items-center justify-center transition-colors ${canNext ? 'text-nw-black hover:border-nw-gold hover:text-nw-gold' : 'text-[#00000030]'}`}>
        <ChevronRight size={18} />
      </button>
    </div>
  );
}
